import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { User, Building2, Mail, Users, TrendingUp } from 'lucide-react';

const LeadCaptureModal = ({ isOpen, onClose, onSubmit, isSubmitting, calculationSummary }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    company_headcount: ''
  });

  const [errors, setErrors] = useState({});

  const headcountOptions = [
    { value: 'less_300', label: 'Less than 300' },
    { value: 'over_300', label: 'Over 300' },
    { value: 'over_1000', label: 'Over 1,000' },
    { value: 'over_3000', label: 'Over 3,000' },
    { value: 'over_5000', label: 'Over 5,000' },
    { value: 'over_10000', label: 'Over 10,000' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (!formData.company_headcount) {
      newErrors.company_headcount = 'Company size is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg mx-4">
        <DialogHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary-500 rounded-full">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold ">
            Get Your Calculation Results
          </DialogTitle>
          <DialogDescription className=" mt-2">
            To view your comprehensive attrition cost analysis, please provide
            your contact information below.
          </DialogDescription>
        </DialogHeader>

        {calculationSummary && (
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-lg mb-6 border border-green-200">
            <div className="flex items-center space-x-2 mb-2">
              <Building2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                Calculation Preview
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-600">Position</p>
                <p className="font-medium ">
                  {calculationSummary.positionLabel}
                </p>
              </div>
              <div>
                <p className="text-slate-600">Annual Salary</p>
                <p className="font-medium ">
                  ${calculationSummary.annualSalary?.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className="text-sm font-semibold  flex items-center space-x-2"
              >
                <User className="h-4 w-4" />
                <span>Full Name *</span>
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="e.g. John Smith"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`h-11 border-slate-300 focus:border-slate-500 transition-colors ${
                  errors.name ? "border-red-500 focus:border-red-500" : ""
                }`}
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-sm font-semibold  flex items-center space-x-2"
              >
                <Mail className="h-4 w-4" />
                <span>Email Address *</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="e.g. john@company.com"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`h-11 border-slate-300 focus:border-slate-500 transition-colors ${
                  errors.email ? "border-red-500 focus:border-red-500" : ""
                }`}
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="company"
                className="text-sm font-semibold  flex items-center space-x-2"
              >
                <Building2 className="h-4 w-4" />
                <span>Company Name *</span>
              </Label>
              <Input
                id="company"
                type="text"
                placeholder="e.g. Acme Corporation"
                value={formData.company_name}
                onChange={(e) =>
                  handleInputChange("company_name", e.target.value)
                }
                className={`h-11 border-slate-300 focus:border-slate-500 transition-colors ${
                  errors.company_name
                    ? "border-red-500 focus:border-red-500"
                    : ""
                }`}
                disabled={isSubmitting}
              />
              {errors.company_name && (
                <p className="text-sm text-red-600">{errors.company_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="headcount"
                className="text-sm font-semibold  flex items-center space-x-2"
              >
                <Users className="h-4 w-4" />
                <span>Company Size *</span>
              </Label>
              <Select
                value={formData.company_headcount}
                onValueChange={(value) =>
                  handleInputChange("company_headcount", value)
                }
                disabled={isSubmitting}
              >
                <SelectTrigger
                  className={`h-11 border-slate-300 focus:border-slate-500 ${
                    errors.company_headcount ? "border-red-500" : ""
                  }`}
                >
                  <SelectValue placeholder="Select company size" />
                </SelectTrigger>
                <SelectContent>
                  {headcountOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.company_headcount && (
                <p className="text-sm text-red-600">
                  {errors.company_headcount}
                </p>
              )}
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="text-xs text-slate-600 text-center">
              By submitting this form, you agree to receive communication from
              Betterteem AI® regarding workforce management insights and
              solutions.
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 h-11 border-slate-300 hover:bg-slate-50 "
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              // className="flex-1 h-11 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white font-semibold"
              className="flex-1 h-11 bg-[#24202C] hover:bg-[#1D1A24] disabled:bg-[#D3D2D4] text-white font-semibold rounded-lg "
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                "Get Results"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;